/**
 * 客户端拆字分析
 * 输入：中文名字
 * 输出：字义、部首、笔画、主题词
 */

import radicalsData from "../data/radicals.json";
import charsData from "../data/chars.json";
import radicalMapData from "../data/radicalToOrgan.json";
import type { CharAnalysis, CharInfo } from "../shared/types";

interface RadicalsDB {
  radicals: Record<string, {
    name: string;
    stroke: number;
    category: string;
    alias?: string[];
  }>;
}

interface CharsDB {
  chars: Record<string, {
    pinyin: string;
    structure: string;
    strokes: number;
    meanings: string[];
  }>;
}

interface RadicalMapDB {
  radicalMap: Record<string, string>;
  defaultMap: string;
}

const radicals = (radicalsData as RadicalsDB).radicals;
const chars = (charsData as CharsDB).chars;
const radicalMap = (radicalMapData as RadicalMapDB).radicalMap;
const radicalMapValues = new Set(Object.values(radicalMap));

const RADICAL_KEYS = new Set(Object.keys(radicals));

const CHAR_RADICALS: Record<string, string[]> = {
  "王": ["王"], "李": ["木"], "张": ["弓"], "刘": ["刂"], "陈": ["阝"],
  "杨": ["木"], "黄": ["黄"], "赵": ["走"], "周": ["口"], "吴": ["口"],
  "徐": ["彳"], "孙": ["子"], "朱": ["木"], "马": ["马"], "胡": ["月"],
  "郭": ["阝"], "林": ["木"], "何": ["亻"], "高": ["高"], "铁": ["钅"],
  "柱": ["木"], "明": ["日"], "伟": ["亻"], "芳": ["艹"], "娜": ["女"],
  "敏": ["攵"], "静": ["青"], "丽": ["一"], "强": ["弓"], "磊": ["石"],
  "军": ["冖"], "洋": ["氵"], "勇": ["力"], "艳": ["丰"], "杰": ["木"],
  "娟": ["女"], "涛": ["氵"], "超": ["走"], "秀": ["禾"], "英": ["艹"],
  "平": ["干"], "刚": ["冈"], "桂": ["木"], "文": ["文"], "华": ["十"],
  "建": ["廴"], "斌": ["文"], "婷": ["女"], "慧": ["心"], "欣": ["斤"],
  "浩": ["氵"], "然": ["灬"], "子": ["子"], "轩": ["车"], "梓": ["木"],
  "萱": ["艹"], "雨": ["雨"], "诗": ["讠"], "梦": ["夕"], "思": ["心"],
  "嘉": ["加"], "瑞": ["王"], "天": ["大"], "满": ["氵"], "婉": ["女"],
  "清": ["氵"], "阿": ["阝"], "牛": ["牛"], "逍": ["辶"], "遥": ["辶"],
  "仑": ["人"], "玉": ["王"], "龙": ["龙"], "宇": ["宀"], "宙": ["宀"],
  "涵": ["氵"], "冰": ["冫"], "雪": ["雨"], "月": ["月"], "光": ["儿"],
  "星": ["日"], "辰": ["辰"], "夕": ["夕"], "云": ["二"], "风": ["风"],
  "雷": ["雨"], "电": ["电"], "火": ["火"], "水": ["水"], "木": ["木"],
  "土": ["土"], "金": ["金"], "山": ["山"], "石": ["石"], "田": ["田"],
  "人": ["人"], "心": ["心"], "手": ["手"], "口": ["口"], "目": ["目"],
  "耳": ["耳"], "足": ["足"], "女": ["女"], "日": ["日"], "鬼": ["鬼"],
  "神": ["礻"], "魔": ["麻"], "妖": ["女"], "兽": ["犬"], "虫": ["虫"],
  "鸟": ["鸟"], "鱼": ["鱼"], "花": ["艹"], "草": ["艹"], "树": ["木"],
  "根": ["木"], "叶": ["艹"], "红": ["纟"], "白": ["白"], "黑": ["黑"],
  "紫": ["糸"], "绿": ["纟"], "蓝": ["纟"], "彩": ["彡"], "无": ["无"],
  "名": ["口"], "字": ["宀"], "一": ["一"], "小": ["小"], "三": ["一"],
};

const CHAR_TAGS: Record<string, string[]> = {
  "王": ["王权", "统治", "三横一竖", "姓氏"],
  "铁": ["金属", "坚硬", "重", "黑"],
  "柱": ["支撑", "竖立", "木质", "中心"],
  "林": ["森林", "树木", "双木"],
  "明": ["光亮", "日月", "智慧"],
  "雪": ["寒冷", "白色", "飘落"],
  "冰": ["寒冷", "坚硬", "晶体"],
  "水": ["流体", "透明", "滋养"],
  "火": ["炽热", "光明", "破坏"],
  "风": ["流动", "无形", "自由"],
  "雨": ["流动", "云层", "滋养"],
  "雷": ["巨响", "电光", "天威"],
  "龙": ["神话", "威严", "鳞片", "长身"],
  "魔": ["黑暗", "异常", "无形"],
  "鬼": ["幽灵", "阴冷", "半透明"],
  "神": ["光环", "神圣", "威能"],
  "星": ["光点", "夜空", "遥远"],
  "月": ["柔和", "周期", "银色"],
  "日": ["炽热", "圆形", "光芒"],
  "花": ["鲜艳", "芬芳", "绽放"],
  "草": ["柔软", "绿色", "低矮"],
  "树": ["高大", "木质", "年轮"],
  "虫": ["多足", "蠕动", "小"],
  "鸟": ["羽毛", "飞翔", "喙"],
  "鱼": ["鳞片", "水生", "鳍"],
  "兽": ["毛皮", "利爪", "野性"],
  "石": ["坚硬", "沉重", "灰色"],
  "山": ["高大", "陡峭", "稳定"],
  "土": ["厚重", "基础", "黄"],
  "金": ["金属", "光泽", "重"],
  "玉": ["温润", "半透", "白绿"],
  "云": ["飘逸", "白", "不定形"],
  "梦": ["不定形", "紫", "模糊"],
  "思": ["脑部", "无形", "持续"],
};

const ORGAN_NAMES: Record<string, string> = {
  "metal_scale": "金属鳞",
  "wood_shell": "树皮壳",
  "fluid_body": "流体身",
  "flame_aura": "火焰光环",
  "earth_skin": "土质皮肤",
  "stone_armor": "岩石甲",
  "mountain_back": "山型背",
  "flesh_pulse": "血肉肌",
  "heart_organ": "心脏",
  "multi_eye": "多眼",
  "long_ear": "长耳",
  "many_mouth": "多嘴",
  "insect_leg": "虫足",
  "wing": "翅膀",
  "dragon_horn": "龙角",
  "ghost_aura": "幽灵雾",
  "halo": "光环",
  "humanoid_body": "人形身",
  "soft_limb": "柔肢",
  "beast_leg": "兽足",
  "mist_body": "雾身",
  "flowing_form": "流动形",
  "crown": "王冠",
  "leather_wing": "革翼",
  "thread_tendril": "丝触手",
  "bright_eye": "亮眼",
  "dark_eye": "暗眼",
  "tear_streak": "泪痕",
  "golden_horn": "金角",
  "blood_vein": "血纹",
  "leaf_fin": "叶鳍",
  "abstract_blob": "抽象核",
};

function detectRadical(char: string): string[] {
  return CHAR_RADICALS[char] ?? [];
}

function extractCharTags(char: string, meanings: string[]): string[] {
  const tags: string[] = [];
  const known = CHAR_TAGS[char];
  if (known) tags.push(...known);
  if (meanings.some((m) => m.includes("surname"))) tags.push("姓氏");
  return tags;
}

export function analyze(name: string): CharAnalysis {
  const trimmed = name.trim();
  const charsArr: CharInfo[] = [];
  const themes: string[] = [];
  const radicalSummary: string[] = [];

  for (const c of trimmed) {
    const cd = chars[c];
    if (cd) {
      const rads = detectRadical(c);
      const tags = extractCharTags(c, cd.meanings);
      charsArr.push({
        char: c,
        pinyin: cd.pinyin,
        radicals: rads,
        strokes: cd.strokes,
        structure: cd.structure,
        meanings: cd.meanings,
      });
      for (const t of tags) {
        if (!themes.includes(t)) themes.push(t);
      }
      for (const r of rads) {
        const organ = radicalMap[r] ?? "abstract_blob";
        const organName = ORGAN_NAMES[organ] ?? organ;
        radicalSummary.push(`${r}→${organName}`);
      }
    } else {
      charsArr.push({
        char: c,
        pinyin: "?",
        radicals: [],
        strokes: 0,
        structure: "未知",
        meanings: ["未知字符"],
      });
    }
  }

  return {
    raw: trimmed,
    chars: charsArr,
    themes: themes.slice(0, 8),
    radicalSummary: radicalSummary.slice(0, 6),
  };
}

export function isValidName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 8) return false;
  for (const c of trimmed) {
    if (!RADICAL_KEYS.has(c) && !chars[c]) {
      if (c.match(/[一-龥]/) === null) return false;
    }
  }
  return true;
}
